import type { FieldTree } from '@angular/forms/signals';
import type { CustomButtonEventPayload } from '@components/sp-custom-button';
import type { ButtonAdditionalEffectsSettings } from '@components/sp-button-additional-effects-settings/type/button-additional-effects-settings.type';
import type { ButtonIcon } from '@components/sp-button-icon/type/button-icon.type';
import type { ShadowSettings } from '@common/types/shadow-settings.type';
import type { ESize } from '@common/enums';

import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    HostListener,
    Output,
    effect,
    input,
    untracked,
} from '@angular/core';
import { FormField } from '@angular/forms/signals';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';

import { ERadioGroupType } from '@ui/sp-radio-group';
import { EButtonStyleType, EButtonWidthType } from '@common/enums';
import { CSS_RESTRICTIONS } from '@common/constants';
import { isEmptyButtonLabel } from '@common/helpers/forms';
import { EPixelTypes } from '@common/types/analytics-setings.type';

import type { AnalyticsSettingsLegacy, ButtonItem } from '@sites/entity-core';
import type { ButtonForm } from '@sites/entity-signal-form';

import {
    BUTTON_LINK_TYPE_PLACEHOLDER,
    LINK_TYPE_NAME,
    LINK_TYPES,
} from '@web/widgets/button/constants/button-widget.const';
import { WebCustomButtonComponent } from '@web/shared/components/web-custom-button/web-custom-button.component';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { WebAnalyticsSettingsSignalLegacyComponent } from '@web/shared/components/web-analytics-settings-signal-legacy/web-analytics-settings-signal-legacy.component';
import { SpButtonIconComponent } from '@components/sp-button-icon/sp-button-icon.component';
import { SpShadowSettingsSignalLegacyComponent } from '@components/sp-shadow-settings-signal-legacy';

/**
 * Per-button editor — fully signal-based. Receives the shared widget form
 * plus the index of the button being edited; every write goes through
 * `form.updateButton(index, patch)` (or a `[formField]` binding on the item's
 * FieldTree leaves), so the preview updates live.
 *
 * Save/cancel semantics are owned by the parent control: it opens a form
 * transaction before mounting this component, commits on `saveEvent` and
 * rolls back on `cancelEvent` — replacing the FormGroup era's
 * cached-initial-value reset in `WebBaseControlTypedSettingsComponent`.
 */
@Component({
    selector: 'web-button-settings',
    templateUrl: './button-settings.component.html',
    styleUrls: ['./button-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonElementsModule,
        WebCustomButtonComponent,
        BsDropdownModule,
        WebAnalyticsSettingsSignalLegacyComponent,
        SpButtonIconComponent,
        SpShadowSettingsSignalLegacyComponent,
        FormField,
    ],
})
export class ButtonSettingsComponent {
    public readonly form = input.required<ButtonForm>();
    public readonly index = input.required<number>();
    public readonly isSpecificWidget = input<boolean>(false);
    public readonly isZeroWidget = input<boolean>(false);

    @Output() public saveEvent: EventEmitter<void> = new EventEmitter();
    @Output() public cancelEvent: EventEmitter<void> = new EventEmitter();

    public readonly EButtonStyleType = EButtonStyleType;
    public readonly EButtonWidthType = EButtonWidthType;
    public readonly ERadioGroupType = ERadioGroupType;
    public readonly LINK_TYPE_NAME = LINK_TYPE_NAME;
    public readonly LINK_TYPES = LINK_TYPES;
    public readonly BUTTON_LINK_TYPE_PLACEHOLDER = BUTTON_LINK_TYPE_PLACEHOLDER;
    public readonly CSS_RESTRICTIONS = CSS_RESTRICTIONS;
    protected readonly EPixelTypes = EPixelTypes;

    public isShowAnalyticsSettings: boolean = false;

    constructor() {
        // Mirror of the FormGroup era's `initSubscriptions`: leaving a t.me
        // URL drops the "prevent link converting" flag.
        effect(() => {
            const item = this.itemField;
            if (!item) return;

            const url = item.link.url().value();
            const preventLinkConverting = item.preventLinkConverting().value();

            if (!url?.includes('t.me') && preventLinkConverting) {
                untracked(() => this.patch({ preventLinkConverting: false }));
            }
        });
    }

    /** FieldTree of the edited button — `undefined` until the form holds the index. */
    public get itemField(): FieldTree<ButtonItem> | undefined {
        return this.form().buttonAt(this.index());
    }

    /** Value snapshot of the edited button. */
    public get itemValue(): ButtonItem | undefined {
        return this.itemField?.().value();
    }

    /**
     * Button text is required: blank text renders an unusable button, so the
     * field is highlighted and both Save and the Enter shortcut are blocked.
     */
    public get saveDisablePredicate(): boolean {
        return isEmptyButtonLabel(this.itemValue?.label);
    }

    private patch(patch: Partial<ButtonItem>): void {
        this.form().updateButton(this.index(), patch);
    }

    // --- Simple fields ---

    public setSize(value: string): void {
        this.patch({ size: value as ESize });
    }

    public setStyleType(event: Event): void {
        const value = (event.target as HTMLSelectElement).value as ButtonItem['styleType'];
        this.patch({ styleType: value });
    }

    public setWidthType(event: Event): void {
        const value = (event.target as HTMLSelectElement).value as ButtonItem['widthType'];
        this.patch({ widthType: value });
    }

    public setWidthValue(value: number): void {
        this.patch({ widthValue: value });
    }

    public setLinkType(linkType: { type: ButtonItem['link']['type'] }): void {
        const link = this.itemValue?.link;
        if (!link || linkType.type === link.type) {
            return;
        }

        this.patch({ link: { ...link, type: linkType.type } });
    }

    public toggleTargetBlank(value: boolean): void {
        this.patch({ isTargetBlank: value });
    }

    public togglePreventLinkConverting(value: boolean): void {
        this.patch({ preventLinkConverting: value });
    }

    public setNewElementId(newId: string): void {
        this.patch({ elementId: newId });
    }

    // --- Style groups ---

    public customButtonChangeHandler(changes: CustomButtonEventPayload): void {
        const patch: Partial<ButtonItem> = {
            buttonBase: changes.base as unknown as ButtonItem['buttonBase'],
            buttonHover: changes.hover as unknown as ButtonItem['buttonHover'],
        };

        if (changes?.isIndependentCorners !== undefined) {
            patch.isIndependentCorners = changes.isIndependentCorners;
        }
        if (changes?.radiusSettings) {
            patch.advancedRadius = changes.radiusSettings as ButtonItem['advancedRadius'];
        }
        if (changes?.advancedBorderSettings !== undefined) {
            patch.advancedBorderSettings =
                changes.advancedBorderSettings as unknown as ButtonItem['advancedBorderSettings'];
        }

        this.patch(patch);
    }

    public buttonIconChangeHandler(changes: ButtonIcon): void {
        this.patch({ buttonIcon: changes as unknown as ButtonItem['buttonIcon'] });
    }

    public additionalEffectsChangeHandler(changes: ButtonAdditionalEffectsSettings): void {
        this.patch({ additionalEffects: changes as unknown as ButtonItem['additionalEffects'] });
    }

    public setShadow(value: boolean): void {
        this.patch({ isShadow: value });
    }

    public setShadowSettings(settings: ShadowSettings): void {
        this.patch({ shadowSettings: settings });
    }

    // --- Analytics ---

    public setAnalyticsSettings(analyticsSettings: AnalyticsSettingsLegacy): void {
        this.patch({ analyticsSettings });
    }

    public setSettingsView(): void {
        this.isShowAnalyticsSettings = !this.isShowAnalyticsSettings;
    }

    public get isEnabledAnalytic(): boolean {
        const settings = this.itemValue?.analyticsSettings;
        return Boolean(settings?.googleAnalytic || settings?.pixelAnalytic);
    }

    // --- Save / cancel ---

    public onSave(): void {
        if (this.saveDisablePredicate) {
            return;
        }

        this.saveEvent.emit();
    }

    public onReset(): void {
        this.cancelEvent.emit();
    }

    @HostListener('document:keydown.enter')
    public onEnterKeyPress(): void {
        this.onSave();
    }

    @HostListener('document:keydown.escape', ['$event'])
    public onEscapeKeyPress(event: KeyboardEvent): void {
        event.stopPropagation();
        this.onReset();
    }
}
